import { CustomCard } from '@/components/custom-card';
import { SubheadingSm } from '@/components/custom-titles';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/numberFormat';
import { useTranslation } from 'react-i18next';
import { CONTRACT_LOADS, VALUE_COLORS } from '../constants';
import { HarvestPlacesSummary } from '../model';

interface PlacesSummaryProps {
  title: string;
  summary?: HarvestPlacesSummary;
  className?: string;
  /** Klasterlar sahifasida klasterlar soni birinchi turadi. */
  clustersFirst?: boolean;
}

/** Shartnoma turlari bo'yicha progress barlar + uchta umumiy son.
 *  Maskanlar va klasterlar sahifalarida bir xil ishlatiladi. */
export const PlacesSummary = ({
  title,
  summary,
  className,
  clustersFirst,
}: PlacesSummaryProps) => {
  const { t } = useTranslation();
  const loads = summary?.loads;

  const contractLoads = CONTRACT_LOADS.map(
    ({ labelKey, netKey, planKey, barColor }) => {
      const net = loads?.[netKey] || 0;
      const plan = loads?.[planKey] || 0;
      return {
        title: t(labelKey),
        barColor,
        percent: plan ? Math.round((net / plan) * 100) : 0,
        volume: `${t('delivered_weight')} ${formatNumber(net)} t / ${formatNumber(plan)} t`,
      };
    },
  );

  const places = {
    title: t('total_places_count'),
    value: formatNumber(summary?.places_count || 0),
  };
  const clusters = {
    title: t('total_clusters_count'),
    value: formatNumber(summary?.companies_count?.clusters_count || 0),
  };

  const counts = [
    ...(clustersFirst ? [clusters, places] : [places, clusters]),
    {
      title: t('count_of_cooperations'),
      value: formatNumber(summary?.companies_count?.cooperation_count || 0),
    },
  ];

  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      <CustomCard className="flex w-full flex-col gap-6">
        <SubheadingSm title={title} />

        <div className="flex flex-col gap-5">
          {contractLoads.map((item) => (
            <div key={item.title} className="flex w-full flex-col gap-2">
              <div className="flex w-full justify-between">
                <span className="text-sm font-medium text-foreground">
                  {item.title}
                </span>
                <span className="text-sm font-normal text-foreground opacity-50">
                  {item.percent}%
                </span>
              </div>

              <div className="h-1.5 w-full rounded-full bg-white/10">
                <div
                  className={cn('h-1.5 rounded-full', item.barColor)}
                  /* Foiz 100 dan oshsa bar konteynerdan chiqib ketmasin. */
                  style={{ width: `${Math.min(item.percent, 100)}%` }}
                />
              </div>

              <span className="text-xs font-normal text-foreground opacity-50">
                {item.volume}
              </span>
            </div>
          ))}
        </div>
      </CustomCard>

      <div className="flex w-full gap-6">
        {counts.map((item) => (
          <CustomCard key={item.title} className="flex w-full flex-col gap-2">
            <span className="text-sm font-medium text-foreground">
              {item.title}
            </span>
            <span className={cn('text-3xl font-semibold', VALUE_COLORS.green)}>
              {item.value} {t('ta')}
            </span>
          </CustomCard>
        ))}
      </div>
    </div>
  );
};
